<template>
    <page-container class="h-full">
        <page-header>{{ props.title }}</page-header>

        <iframe
            ref="dashboardIframe"
            :src="props.embed.url"
            class="w-full min-h-[500px] border-0 rounded-lg"
            allow="fullscreen"
            referrerpolicy="strict-origin-when-cross-origin"
        />
    </page-container>
</template>

<script lang="ts" setup>
import { ref, onMounted, onUnmounted } from 'vue';
import PageContainer from '~/components/general/PageContainer.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import type {
    OmniEmbed,
    OmniDateFilter,
    OmniDashboardFiltersPayload,
    OmniEmbedEventData,
    OmniSizeEventPayload,
} from '~/types/omni.types';

const props = defineProps<{
    embed: OmniEmbed;
    title: string;
}>();

const dateFilter: OmniDateFilter | null = props.embed.dateFilter;
const dashboardIframe = ref<HTMLIFrameElement | null>(null);

const setFrameHeight = (height: number) => {
    if (dashboardIframe.value) {
        dashboardIframe.value.style.height = `${height}px`;
    }
};

// Keys that belong to the target filter's identity and must not be overwritten by the source.
const DATE_FILTER_IDENTITY_KEYS = ['topic', 'fieldName', 'base_view', 'label', 'hidden'] as const;

const syncDateFilter = (filters: OmniDashboardFiltersPayload) => {
    if (!dateFilter || !dashboardIframe.value?.contentWindow) {
        return;
    }

    const source = filters[dateFilter.source];
    if (!source) {
        return;
    }

    const target = filters[dateFilter.target];
    const targetFilter = target?.filter ?? {};

    const identity = Object.fromEntries(
        DATE_FILTER_IDENTITY_KEYS.filter((key) => key in targetFilter).map((key) => [key, targetFilter[key]])
    );

    const synced = { ...source.filter, ...identity };

    const filterUrlParameter = `f--${dateFilter.target}=${encodeURIComponent(JSON.stringify(synced))}`;

    dashboardIframe.value.contentWindow.postMessage(
        { name: 'dashboard:filter-change-by-url-parameter', payload: { filterUrlParameter } },
        '*'
    );
};

const listener = (event: MessageEvent<OmniEmbedEventData<OmniSizeEventPayload | OmniDashboardFiltersPayload>>) => {
    if (event.data?.source !== 'omni') {
        return;
    }

    switch (event.data.name) {
        case 'size': {
            const { height } = event.data.payload as OmniSizeEventPayload;
            if (height) {
                setFrameHeight(height);
            }
            break;
        }
        case 'dashboard:filters': {
            syncDateFilter(event.data.payload as OmniDashboardFiltersPayload);
            break;
        }
    }
};

onMounted(() => {
    window.addEventListener('message', listener);
});

onUnmounted(() => {
    window.removeEventListener('message', listener);
});
</script>
